import { useOfflineContext } from 'contexts/OfflineContext/OfflineContext';
import React, { useEffect, useRef } from 'react';
import Grid from '@mui/material/Grid2';
import { Box, Stack, Card, Typography, styled } from '@mui/material';
import { Error } from '@mui/icons-material';
import { Button } from 'components/Button/Button';
import { Alert } from 'components/Alert/Alert';
import { AssessmentAuthenticationField } from 'features/assessments/types';
import { generateHeaderText } from 'features/assessments/utils/utils';
import LoginBackground from 'assets/cobrand/wellframe/WellframeLoginBackground.svg';
import AssessmentsAuthenticationbackground from 'assets/images/assessments/AssessmentsAuthenticationbackground.svg';
import WellframeIcon from 'assets/images/assessments/wellframeIcon.svg';
import { useDeviceType } from 'contexts/ThemeContext/hooks/useDeviceType';
import { useAssessmentsAuth } from 'features/assessments/hooks/useAssessmentsAuth';
import { useAssessmentsAuthConfig } from 'features/assessments/hooks/useAssessmentsAuthConfig';
import { AssessmentsNotFound } from 'components/AssessmentsNotFound/AssessmentsNotFound';
import { useAssessmentsLoginTracking } from 'features/assessments/pendo/useAssessmentsLoginTracking';
import { AssessmentsCompleted } from 'components/AssessmentsCompleted/AssessmentsCompleted';
import { AssessmentsAuthenticationFieldComponent } from './AssessmentsAuthenticationField';

interface AssessmentsAuthenticationProps {
  fields: AssessmentAuthenticationField[];
}

const BackgroundContainer = styled(Grid)(({ theme }) => ({
  height: '100vh',
  backgroundImage: `url(${LoginBackground})`,
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'center',
  backgroundSize: 'cover',
  overflowY: 'auto',
  padding: theme.spacing(3),
  [theme.breakpoints.down('sm')]: {
    alignItems: 'flex-start',
    backgroundImage: `url(${AssessmentsAuthenticationbackground})`,
  },
}));

const FormContainer = styled(Card)(({ theme }) => ({
  boxSizing: 'border-box',
  padding: theme.spacing(8),
  paddingBottom: theme.spacing(4),
  maxWidth: '540px',
  width: '100%',
  backgroundColor: theme.palette.background.paper,
  boxShadow: theme.shadows[4],
  [theme.breakpoints.down('sm')]: {
    maxWidth: '100%',
    padding: theme.spacing(3),
    paddingTop: theme.spacing(5),
  },
}));

const PoweredByContainer = styled(Stack)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginTop: theme.spacing(2.5),
}));

const HeaderTypography = styled(Typography)(({ theme }) => ({
  alignSelf: 'center',
  textAlign: 'center',
  maxWidth: '85%',
  [theme.breakpoints.down('sm')]: {
    lineHeight: '25px',
  },
}));

export const AssessmentsAuthentication = ({ fields }: AssessmentsAuthenticationProps) => {
  const { isOnline } = useOfflineContext();
  const { isMobile } = useDeviceType();
  const { isShowNotFound: authConfigNotFound } = useAssessmentsAuthConfig();

  const {
    formMethods: { control },
    onSubmit,
    isSubmitting,
    errorMessage,
    showNotFound,
    isShowingAlertError,
    alertError,
    saveAndCloseCompleted,
  } = useAssessmentsAuth();
  const { handleDismiss, position, message } = alertError;
  const { trackLoginPageViewed } = useAssessmentsLoginTracking();
  const hasTrackedPageView = useRef(false);

  useEffect(() => {
    const isFormVisible = !showNotFound && !authConfigNotFound && !saveAndCloseCompleted;
    if (!isFormVisible || hasTrackedPageView.current) return;
    hasTrackedPageView.current = true;
    trackLoginPageViewed();
  }, [showNotFound, authConfigNotFound, saveAndCloseCompleted, trackLoginPageViewed]);

  // Show not found screen for 404 errors from fhir-configuration or form submission
  if (showNotFound || authConfigNotFound) {
    return <AssessmentsNotFound hideBackHomeButton />;
  }

  // Show completed screen for completed assessments
  if (saveAndCloseCompleted) {
    return <AssessmentsCompleted />;
  }

  return (
    <BackgroundContainer container justifyContent="center" alignItems="center">
      <FormContainer>
        <form
          id="assessments-authentication-form"
          data-testid="assessments-authentication-form"
          onSubmit={onSubmit}
        >
          <Stack direction="column" spacing={4}>
            <HeaderTypography variant={isMobile ? 'title3' : 'title1'} color="primary">
              {generateHeaderText(fields)}
            </HeaderTypography>
            <Box>
              <Stack spacing={2}>
                {fields.map((field) => (
                  <AssessmentsAuthenticationFieldComponent
                    key={field.key}
                    field={field}
                    control={control}
                    externalError={!!errorMessage}
                  />
                ))}
              </Stack>
              {errorMessage && (
                <Typography color="error" mt={1}>
                  {errorMessage}
                </Typography>
              )}
            </Box>
            <Button
              id="assessments-authentication-form__submit-button"
              isLoading={isSubmitting}
              variant="primary"
              disabled={isSubmitting || !isOnline}
              type="submit"
              size="xlarge"
              circularProgressProps={{ size: 24 }}
              aria-label="Next"
            >
              Next
            </Button>
            <PoweredByContainer direction="row" spacing={1}>
              <img width="24px" height="24px" src={WellframeIcon} alt="Wellframe" />
              <Typography variant="body2" color="grey.800">
                Powered by Wellframe
              </Typography>
            </PoweredByContainer>
          </Stack>
        </form>
      </FormContainer>
      <Alert
        isPresented={isShowingAlertError}
        variant="filled"
        severity="error"
        message={message}
        icon={<Error />}
        anchorOrigin={{ vertical: position, horizontal: 'center' }}
        handleDismiss={handleDismiss}
        snackbarSx={{ position: 'fixed' }}
      />
    </BackgroundContainer>
  );
};
